// Enhancement layer for /election-timeline-calculator.
// The page is fully server-side rendered — this script adds live recalculation
// as the user changes inputs, without requiring a form submit or page reload.
// Falls back gracefully to the SSR form submit if JS is unavailable.
//
// iOS Safari fires `input` on the native date spinner as the user scrolls, but
// `change` only fires (unreliably) when the picker is dismissed. We listen to
// both and let the debounce deduplicate any rapid-fire calls.

function initTimelineCalculator() {
  const form            = document.getElementById('timeline-form');
  const typeSelect      = document.getElementById('type');
  const dateInput       = document.getElementById('date');
  const electronicField = document.getElementById('electronic-voting-field');

  // Guard: prevent double-initialization when both DOMContentLoaded and
  // turbolinks:load fire on the same page load.
  if (!form || !typeSelect || !dateInput) return;
  if (form.dataset.timelineInitialized) return;
  form.dataset.timelineInitialized = 'true';

  const ELECTRONIC_HIDDEN_TYPES = new Set(['special-assessment']);

  const CALCULATE_PATH = '/timeline/calculate';

  const TYPE_LABELS = {
    'annual':             'Annual Election',
    'acclamation':        'Election by Acclamation',
    'recall':             'Recall Election',
    'special-assessment': 'Special Assessment Vote',
    'change-docs':        'Change in Governing Documents'
  };

  const GUIDE_LINK_CLASS = 'text-[#491363] text-xs tracking-widest uppercase underline hover:text-[#a146b4] transition duration-150';

  // ── Helpers ────────────────────────────────────────────────────────────

  function todayISO() {
    return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
  }

  function formatDate(isoString) {
    if (!isoString) return '';
    const [y, m, d] = isoString.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  }

  function daysFromToday(isoString) {
    if (!isoString) return null;
    const [y, m, d] = isoString.split('-').map(Number);
    const target    = new Date(y, m - 1, d);
    const today     = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((target - today) / 86400000);
  }

  function daysLabel(days) {
    if (days === null) return '';
    if (days > 0)      return `${days} days from today`;
    if (days === 0)    return 'Today';
    return 'Date passed';
  }

  function daysClass(days) {
    if (days === null) return '';
    if (days > 0)      return 'text-gray-400 text-xs mt-1';
    if (days === 0)    return 'text-[#a146b4] text-xs mt-1 tracking-widest uppercase';
    return 'text-red-400 text-xs mt-1';
  }

  // ── Build milestone block ──────────────────────────────────────────────

  function milestoneRow(m, i) {
    const days = daysFromToday(m.date);
    return `
      <div class="py-7 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 sm:gap-6 items-start"
           data-milestone-key="${i}">
        <div>
          <span class="text-[#d4b8dd] text-xs font-light tabular-nums">${String(i + 1).padStart(2, '0')}</span>
          <h3 class="text-sm font-light text-gray-900 mt-1 mb-1">${m.label}</h3>
          ${m.url
            ? `<a href="${m.url}" target="_blank" rel="noopener noreferrer" class="text-[#a146b4] text-xs tracking-widest uppercase mb-2 block underline hover:text-[#491363] transition duration-150">${m.code}</a>`
            : `<p class="text-[#a146b4] text-xs tracking-widest uppercase mb-2">${m.code}</p>`}
          ${m.description ? `<p class="text-gray-400 text-xs leading-relaxed">${m.description}</p>` : ''}
          ${m.guide_url ? `<a href="${m.guide_url}" class="${GUIDE_LINK_CLASS} mt-2 inline-block">${m.guide_label}</a>` : ''}
        </div>
        <div class="sm:text-right">
          <p class="milestone-date text-gray-900 text-sm font-light whitespace-nowrap">${m.formatted || ''}</p>
          <p class="${daysClass(days)} milestone-days">${daysLabel(days)}</p>
        </div>
      </div>
    `;
  }

  function buildWrapElement(data) {
    const wrap       = document.createElement('div');
    wrap.id          = 'milestone-results';
    wrap.className   = 'p-10 sm:p-12 border-b border-[#e4dce9]';
    const guideUrl   = data.election_guide_url;
    const guideLink  = guideUrl
      ? `<a href="${guideUrl}" id="election-type-guide" class="${GUIDE_LINK_CLASS}">${TYPE_LABELS[data.election_type] || 'Election'} Guide →</a>`
      : '';
    wrap.innerHTML = `
      <p class="text-[#a146b4] text-xs tracking-widest uppercase mb-2">Timeline</p>
      <h2 class="text-xl font-light text-gray-900 mb-1">${TYPE_LABELS[data.election_type] || 'Election'}</h2>
      <div class="mb-8">
        <p class="text-gray-400 text-xs tracking-widest uppercase ${guideUrl ? 'mb-3' : ''}">${data.election_type === 'acclamation' ? 'Nomination Period Closing' : 'Meeting'}: ${formatDate(data.meeting_date)}</p>
        ${guideLink}
      </div>
      <div class="divide-y divide-[#e4dce9]">
        ${data.milestones.map((m, i) => milestoneRow(m, i)).join('')}
      </div>
    `;
    return wrap;
  }

  // ── Render — always replace so repeated calls never stack blocks ───────

  const PROPOSAL_TYPES = new Set(['annual', 'recall', 'change-docs', 'special-assessment']);

  function renderMilestones(data) {
    const existing = document.getElementById('milestone-results');

    if (existing) {
      existing.replaceWith(buildWrapElement(data));
    } else {
      const container = document.getElementById('timeline-calculator-public');
      const inputPane = container.querySelector('.p-10.sm\\:p-12.border-b');
      inputPane.after(buildWrapElement(data));
    }

    const conv = document.getElementById('conversion-block');
    if (conv) {
      conv.classList.toggle('hidden', !PROPOSAL_TYPES.has(data.election_type));
      const link = document.getElementById('proposal-link');
      if (link) {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set('date', data.meeting_date);
        url.searchParams.set('type', data.election_type);
        link.href = url.toString();
      }
    }
  }

  // ── Date input label ───────────────────────────────────────────────────

  function updateDateLabel() {
    const label = document.getElementById('date-label');
    if (!label) return;
    label.textContent = typeSelect.value === 'acclamation'
      ? 'Nomination Period Closing'
      : 'Meeting / Election Date';
  }

  // ── Electronic voting field visibility ────────────────────────────────

  function updateElectronicFieldVisibility() {
    if (!electronicField) return;
    electronicField.classList.toggle('hidden', ELECTRONIC_HIDDEN_TYPES.has(typeSelect.value));
  }

  function isElectronicHybrid() {
    if (!electronicField || electronicField.classList.contains('hidden')) return false;
    const checked = form.querySelector('input[name="electronic_hybrid"]:checked');
    return checked ? checked.value === 'true' : false;
  }

  // ── Fetch & update ─────────────────────────────────────────────────────

  let debounceTimer = null;

  function fetchAndUpdate() {
    const date = dateInput.value;
    const type = typeSelect.value;

    if (!date || date <= todayISO()) return;

    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      const evParam = isElectronicHybrid() ? '&electronic_hybrid=true' : '';
      fetch(`${CALCULATE_PATH}?date=${encodeURIComponent(date)}&type=${encodeURIComponent(type)}${evParam}`, {
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(r => r.ok ? r.json() : Promise.reject(r.status))
        .then(data => {
          renderMilestones(data);
          pushState(date, type);
        })
        .catch(() => {}); // silently fall back — SSR form submit still works
    }, 300);
  }

  function pushState(date, type) {
    const url = new URL(window.location.href);
    url.searchParams.set('date', date);
    url.searchParams.set('type', type);
    if (isElectronicHybrid()) {
      url.searchParams.set('electronic_hybrid', 'true');
    } else {
      url.searchParams.delete('electronic_hybrid');
    }
    history.pushState({}, '', url.toString());
  }

  // ── Event listeners ────────────────────────────────────────────────────

  typeSelect.addEventListener('change', () => {
    updateElectronicFieldVisibility();
    updateDateLabel();
    fetchAndUpdate();
  });

  form.querySelectorAll('input[name="electronic_hybrid"]').forEach(radio => {
    radio.addEventListener('change', fetchAndUpdate);
  });

  // iOS Safari fires `input` as the user scrolls the native date spinner;
  // desktop browsers fire `change` when the field loses focus or value commits.
  // We listen to both — the 300 ms debounce deduplicates any overlapping calls.
  dateInput.addEventListener('input', fetchAndUpdate);
  dateInput.addEventListener('change', fetchAndUpdate);

  form.addEventListener('submit', e => {
    const date = dateInput.value;
    if (date && date > todayISO()) {
      e.preventDefault();
      fetchAndUpdate();
    }
  });
}

// Support both Turbolinks navigation and standard direct page loads.
// DOMContentLoaded fires on a cold load; turbolinks:load fires on every
// navigation (including the first). The guard inside initTimelineCalculator
// prevents double-initialization when both fire on the same page load.
document.addEventListener('turbolinks:load', initTimelineCalculator);
document.addEventListener('DOMContentLoaded', initTimelineCalculator);
